import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

export default function GrowthChart() {
  const { profile, refresh } = useProfile();
  const [metric, setMetric] = useState("weight");
  const [sex, setSex] = useState(profile?.sex === "maschio" ? "maschio" : "femmina");
  const [series, setSeries] = useState([]);
  const [form, setForm] = useState({ month: "", weight: "", length: "" });

  useEffect(() => {
    if (profile && profile.sex !== "sorpresa") setSex(profile.sex);
  }, [profile]);

  useEffect(() => {
    api.get("/who-percentiles", { params: { sex, metric } })
      .then(({ data }) => setSeries(data.series || []))
      .catch(() => setSeries([]));
  }, [sex, metric]);

  const records = useMemo(() => profile?.growth_records || [], [profile?.growth_records]);

  const chartData = useMemo(() => {
    return series.map((s) => {
      const rec = records.find((r) => Number(r.month) === s.month);
      return {
        month: s.month,
        p3: s.p3, p50: s.p50, p97: s.p97,
        bimbo: rec ? rec[metric] ?? null : null,
      };
    });
  }, [series, records, metric]);

  const unit = metric === "weight" ? "kg" : "cm";
  const label = metric === "weight" ? "Peso" : "Lunghezza";

  const addRecord = async (e) => {
    e.preventDefault();
    if (form.month === "") { toast.error("Seleziona il mese"); return; }
    const val = metric === "weight" ? form.weight : form.length;
    if (!val) { toast.error(`Inserisci ${label.toLowerCase()}`); return; }
    try {
      await api.post(`/profile/${profile.id}/growth`, {
        month: Number(form.month),
        weight: form.weight ? Number(form.weight) : null,
        length: form.length ? Number(form.length) : null,
      });
      await refresh();
      setForm({ month: "", weight: "", length: "" });
      toast.success("Rilevamento aggiunto 🤍");
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const delRecord = async (month) => {
    try {
      await api.delete(`/profile/${profile.id}/growth/${month}`);
      await refresh();
    } catch { toast.error("Errore"); }
  };

  if (!profile) return null;

  return (
    <section id="crescita" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 sm:py-14 lg:py-20">
      <div className="mb-8">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8C7F78]">Crescita OMS</span>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#2D2825] mt-2">Il grafico di crescita del tuo bambino</h2>
        <p className="text-[#5C534E] mt-2 max-w-2xl">
          Percentili 3°, 50° e 97° dell'Organizzazione Mondiale della Sanità (0-12 mesi). Aggiungi i rilevamenti mese per mese.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="inline-flex rounded-full border border-[#EAE3DC] bg-white p-1">
          {[["weight", "Peso"], ["length", "Lunghezza"]].map(([k, l]) => (
            <button key={k} data-testid={`metric-${k}`} onClick={() => setMetric(k)}
              className="rounded-full px-5 py-1.5 text-sm transition-colors"
              style={{ background: metric === k ? "#4E7363" : "transparent", color: metric === k ? "#fff" : "#5C534E" }}>
              {l}
            </button>
          ))}
        </div>
        <div className="inline-flex rounded-full border border-[#EAE3DC] bg-white p-1">
          {[["femmina", "Femmina"], ["maschio", "Maschio"]].map(([k, l]) => (
            <button key={k} data-testid={`sex-${k}`} onClick={() => setSex(k)}
              className="rounded-full px-5 py-1.5 text-sm transition-colors"
              style={{ background: sex === k ? (k === "maschio" ? "#4A7496" : "#C87D75") : "transparent", color: sex === k ? "#fff" : "#5C534E" }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl bg-white border border-[#EAE3DC] p-5" data-testid="who-growth-chart">
          <ResponsiveContainer width="100%" height={380}>
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAE3DC" />
              <XAxis dataKey="month" tick={{ fill: "#8C7F78", fontSize: 12 }}
                label={{ value: "Mesi", position: "insideBottom", offset: -2, fill: "#8C7F78", fontSize: 12 }} />
              <YAxis tick={{ fill: "#8C7F78", fontSize: 12 }} unit={unit} width={55} />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: "1px solid #EAE3DC", fontSize: 13 }}
                formatter={(v, n) => [v != null ? `${v} ${unit}` : "-", n]}
                labelFormatter={(l) => `Mese ${l}`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="p3" name="3° percentile" stroke="#9DC0DB" strokeWidth={1.5} dot={false} strokeDasharray="4 4" />
              <Line type="monotone" dataKey="p50" name="50° percentile" stroke="#A7C4B5" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="p97" name="97° percentile" stroke="#E5A8A0" strokeWidth={1.5} dot={false} strokeDasharray="4 4" />
              <Line type="monotone" dataKey="bimbo" name={`${label} del bimbo`} stroke="#2D2825" strokeWidth={2.5}
                connectNulls dot={{ r: 4, fill: "#2D2825" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl bg-[#FAF8F5] border border-[#EAE3DC] p-5">
          <h3 className="font-display font-medium text-[#2D2825] mb-3">Aggiungi un rilevamento</h3>
          <form onSubmit={addRecord} className="space-y-3">
            <select data-testid="growth-month" value={form.month}
              onChange={(e) => setForm((f) => ({ ...f, month: e.target.value }))}
              className="w-full rounded-xl bg-white border border-[#EAE3DC] h-10 px-3 text-sm">
              <option value="">Mese di vita</option>
              {Array.from({ length: 13 }, (_, i) => i).map((m) => <option key={m} value={m}>{m} mesi</option>)}
            </select>
            <Input data-testid="growth-weight" type="number" step="0.1" placeholder="Peso (kg)"
              value={form.weight} onChange={(e) => setForm((f) => ({ ...f, weight: e.target.value }))}
              className="rounded-xl bg-white border-[#EAE3DC]" />
            <Input data-testid="growth-length" type="number" step="0.1" placeholder="Lunghezza (cm)"
              value={form.length} onChange={(e) => setForm((f) => ({ ...f, length: e.target.value }))}
              className="rounded-xl bg-white border-[#EAE3DC]" />
            <Button data-testid="add-growth-record-button" type="submit"
              className="w-full rounded-full bg-[#4E7363] hover:bg-[#3d5a4e] text-white">
              <Plus size={16} className="mr-1" /> Aggiungi
            </Button>
          </form>

          <div className="mt-5 space-y-2 max-h-48 overflow-y-auto">
            {records.length === 0 && <p className="text-sm text-[#8C7F78]">Nessun rilevamento ancora.</p>}
            {records.map((r) => (
              <div key={r.month} className="flex items-center justify-between rounded-xl bg-white border border-[#EAE3DC] px-3 py-2 text-sm">
                <span className="text-[#5C534E]">
                  <strong>{r.month}m</strong> · {r.weight ? `${r.weight}kg` : ""} {r.length ? `${r.length}cm` : ""}
                </span>
                <button onClick={() => delRecord(r.month)} className="text-[#B85750] hover:opacity-70"
                  data-testid={`delete-growth-${r.month}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
